import { forwardRef } from "react";

import FooterItemWithPopover from "@/components/sidebar/SidebarFooter/FooterItemWithPopover";
import { ProductSwitcherMenu } from "@/components/sidebar/SidebarFooter/ProductSwitcher/ProductSwitcherMenu";
import { ProductSwitcherTrigger } from "@/components/sidebar/SidebarFooter/ProductSwitcher/ProductSwitcherTrigger";
import type { SidebarFooterPopoverType } from "@/components/sidebar/types";
import { DEFAULT_PRODUCT } from "@/constants/products";

import { twMerge } from "tailwind-merge";

type ProductSwitcherWrapperProps = {
  setActivePopover: (popoverType: SidebarFooterPopoverType) => void;
  activePopover?: SidebarFooterPopoverType;
  isPopoverTriggerOrigin: (e: Event) => boolean;
  sidebarMenu?: boolean;
};

/**
 * Product Switcher for the sidebar footer: the "Switch Product" trigger and its
 * popover. Selecting another product opens it in a new tab. `ref` points to the
 * trigger so the footer can tell trigger clicks from outside clicks.
 */
export const ProductSwitcherWrapper = forwardRef<
  HTMLButtonElement,
  ProductSwitcherWrapperProps
>(
  (
    { setActivePopover, activePopover, isPopoverTriggerOrigin, sidebarMenu },
    ref
  ) => {
    const isActive = activePopover === "productSwitcher";

    return (
      <FooterItemWithPopover
        togglePopover={(open) => {
          setActivePopover(open ? "productSwitcher" : "");
        }}
        isPopoverTriggerOrigin={isPopoverTriggerOrigin}
        classNamePopoverContent={twMerge(sidebarMenu && "ml-6")}
        popoverTrigger={
          <ProductSwitcherTrigger
            ref={ref}
            isActive={isActive}
            sidebarMenu={sidebarMenu}
          />
        }
        popoverContent={
          <ProductSwitcherMenu
            onSelect={(product) => {
              setActivePopover("");
              // Skip the current product or one without a URL.
              if (product.key === DEFAULT_PRODUCT || !product.url) return;
              // Open in a new tab. No shared session yet — user may need to
              // log in there. TODO: revisit when SSO internal login lands.
              window.open(product.url, "_blank", "noopener,noreferrer");
            }}
          />
        }
        isOpen={isActive}
      />
    );
  }
);

ProductSwitcherWrapper.displayName = "ProductSwitcherWrapper";

export default ProductSwitcherWrapper;
